// The menu drawer with image tiles (docs/MENU-BESLUITEN.md M1, LICHT-BESLUITEN L21):
// one drawer on every width. The hamburger opens it (on desktop as a drawer from the
// left of 390 px, on a phone over the full width), a category tile opens its second
// level, the back row closes that again. Escape closes the second level, then the
// drawer, and puts the focus back where it came from; Tab stays inside the drawer.
// Replaces the theme's main-menu element for this menu only: main.js switches between
// a phone drawer and a desktop dropdown bar on the 769 px breakpoint, and the desktop
// bar is gone (the round pictures in jc-circles are the main navigation now).
// Uses trapFocus / removeTrapFocus from main.js.

class JcMainMenu extends HTMLElement {
  connectedCallback() {
    this.disclosure = this.querySelector('.main-menu__disclosure');
    this.toggle = this.querySelector('.main-menu__toggle');
    this.content = this.querySelector('.main-menu__content');
    this.overlay = document.querySelector('.js-overlay');
    // The markup opens the details for visitors without JavaScript; closed by default here.
    this.disclosure.open = false;
    this.disclosure.classList.remove('is-open');

    this.toggle.addEventListener('click', (evt) => {
      evt.preventDefault();
      if (this.isOpen()) this.close();
      else this.open();
    });
    this.content.addEventListener('click', (evt) => this.handleNavClick(evt));
    this.addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') this.handleEscape();
    });
    this.closeFromOverlay = () => this.close();
  }

  isOpen() {
    return this.disclosure.classList.contains('is-open');
  }

  open() {
    this.disclosure.open = true;
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    this.overlay?.classList.add('overlay--nav', 'is-visible');
    this.overlay?.addEventListener('click', this.closeFromOverlay);
    // One frame after opening the details, so the fade runs; the focus can move only a
    // frame later, once the drawer's visibility has turned (a transition starts hidden).
    requestAnimationFrame(() => {
      this.disclosure.classList.remove('is-closing');
      this.disclosure.classList.add('is-open');
      requestAnimationFrame(() => {
        trapFocus(this, this.content.querySelector('a[href], button, summary') || this.content);
      });
    });
  }

  close() {
    const child = this.openChild();
    if (child) this.closeChild(child, false);
    this.disclosure.classList.remove('is-open');
    this.disclosure.classList.add('is-closing');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
    this.overlay?.classList.remove('overlay--nav', 'is-visible');
    this.overlay?.removeEventListener('click', this.closeFromOverlay);
    removeTrapFocus(this.toggle);
    setTimeout(() => {
      if (this.isOpen()) return;
      this.disclosure.classList.remove('is-closing');
      this.disclosure.open = false;
    }, 300);
  }

  openChild() {
    return this.content.querySelector('.main-nav > li > details.is-open');
  }

  openChildPanel(details) {
    details.open = true;
    this.content.classList.add('main-menu__content--no-focus');
    requestAnimationFrame(() => {
      details.classList.remove('is-closing');
      details.classList.add('is-open');
      requestAnimationFrame(() => {
        const panel = details.querySelector('.main-nav__child');
        trapFocus(panel, panel.querySelector('a[href], button'));
      });
    });
  }

  closeChild(details, moveFocus = true) {
    details.classList.remove('is-open');
    details.classList.add('is-closing');
    this.content.classList.remove('main-menu__content--no-focus');
    trapFocus(this, moveFocus ? details.querySelector(':scope > summary') : undefined);
    setTimeout(() => {
      if (details.classList.contains('is-open')) return;
      details.classList.remove('is-closing');
      details.open = false;
    }, 300);
  }

  handleNavClick(evt) {
    const toggle = evt.target.closest('.js-toggle');
    const back = evt.target.closest('.js-back');
    if (toggle && this.contains(toggle)) {
      evt.preventDefault();
      const details = toggle.closest('details');
      if (details.classList.contains('is-open')) this.closeChild(details);
      else this.openChildPanel(details);
    } else if (back) {
      evt.preventDefault();
      this.closeChild(back.closest('details'));
    }
  }

  handleEscape() {
    const child = this.openChild();
    if (child) this.closeChild(child);
    else if (this.isOpen()) this.close();
  }
}

customElements.define('jc-main-menu', JcMainMenu);

// Search (LICHT-BESLUITEN L21): the theme's search icon opens the field in the bar
// (main.js StoreHeader). Escape in the field, the close button and a click outside fold
// it back through that same icon and put the focus on it.
(() => {
  const header = document.querySelector('store-header');
  const icon = header?.querySelector('.header__icons .js-show-search');
  if (!header || !icon) return;
  const isOpen = () => header.classList.contains('search-is-visible');
  const closeSearch = () => {
    if (!isOpen()) return;
    icon.click();
    icon.focus();
  };
  header.querySelector('.js-search-close')?.addEventListener('click', closeSearch);
  header.addEventListener('keyup', (evt) => {
    if (evt.key === 'Escape' && evt.target.closest('.header__search')) closeSearch();
  });
  document.addEventListener('click', (evt) => {
    if (isOpen() && !evt.target.closest('.header__search, .js-show-search')) closeSearch();
  });
})();
